import { UserInfo } from '../components/onboarding/steps/ScheduleVisit'
import type {} from '../types'

import api from './api'

export interface CreateCompanyInformationDto {
  companyName: string
  businessName: string
  rfc: string
  email: string
  phoneNumber: string
  website?: string
  businessDescription?: string
  economicActivity: string
  taxRegime: string
  active?: boolean
}

export interface CreateCompanyInformationCompleteDto
  extends CreateCompanyInformationDto {
  businessDescription?: string
  economicActivity: string
  taxRegime: string
  active?: boolean
  street: string
  exteriorNumber: string
  interiorNumber?: string
  betweenStreets: string
  postalCode: string
  neighborhood: string
  city?: string
  municipality?: string
  state: string
  stateAbbreviation?: string
  country?: string
  creditApplicationId?: string
}

export interface CompanyAddress {
  id: string
  type: 'COMPANY' | 'OPERATIVE'
  street: string
  exteriorNumber: string
  interiorNumber?: string | null
  betweenStreets?: string
  neighborhood: string
  postalCode: string
  city?: string
  municipality?: string
  state: string
  stateAbbreviation?: string
  country: string
  reference?: string | null
  isPrimary?: boolean
}

export interface CompanyInformation {
  id: string
  companyName: string
  businessName: string
  rfc: string
  email: string
  phoneNumber: string
  website?: string
  businessDescription?: string
  economicActivity: string
  taxRegime: string
  active: boolean
  verified: boolean
  createdAt: string
  updatedAt: string
  userId: string
  operativeSameAsFiscalAddress?: boolean
  addresses?: CompanyAddress[]
}

export interface CompanyWithAddress extends CompanyInformation {
  address: {
    id: string
    street: string
    exteriorNumber: string
    interiorNumber?: string
    betweenStreets: string
    postalCode: string
    neighborhood: string
    city?: string
    municipality?: string
    state: string
    stateAbbreviation?: string
    country: string
  }
}

class CompanyService {
  async createCompany(
    data: CreateCompanyInformationDto
  ): Promise<CompanyInformation> {
    const response = await api.post('/company-information', data)
    return response.data
  }

  async createCompanyComplete(
    data: CreateCompanyInformationCompleteDto
  ): Promise<CompanyWithAddress> {
    const response = await api.post('/company-information/complete', data)
    console.log('Create company response:', response.data)

    // Handle the response structure {success: true, data: {...}}
    if (response.data.success && response.data.data) {
      return response.data.data
    }

    // Fallback for direct object response
    return response.data
  }

  async getCompanyCalendlyInfo(): Promise<UserInfo> {
    const response = await api.get('/company-information/user-info')
    return response.data.data
  }

  async getCompanies(): Promise<CompanyInformation[]> {
    const response = await api.get('/company-information')
    console.log('Raw API response:', response.data)

    // Handle the response structure {success: true, data: [...]}
    if (response.data.success && response.data.data) {
      return response.data.data
    }

    // Fallback for direct array response
    return response.data || []
  }

  async getCompanyById(id: string): Promise<CompanyInformation> {
    const response = await api.get(`/company-information/${id}`)
    return response.data
  }

  async updateCompany(
    id: string,
    data: Partial<CreateCompanyInformationDto>
  ): Promise<CompanyInformation> {
    const response = await api.patch(`/company-information/${id}`, data)
    return response.data
  }

  async verifyCompany(id: string): Promise<CompanyInformation> {
    const response = await api.patch(`/company-information/${id}/verify`)
    return response.data
  }

  async deleteCompany(id: string): Promise<{ message: string }> {
    const response = await api.delete(`/company-information/${id}`)
    return response.data
  }

  async searchCompanies(
    term: string,
    page: number = 0,
    limit: number = 10
  ): Promise<any> {
    const response = await api.get('/company-information/search', {
      params: { term, page, limit },
    })
    return response.data
  }

  async getCompanyByRfc(rfc: string): Promise<CompanyInformation> {
    const response = await api.get(`/company-information/by-rfc/${rfc}`)
    return response.data
  }

  // Function to save company data (always use complete endpoint for now)
  async saveCompanyData(
    formData: any,
    creditApplicationId?: string | null
  ): Promise<CompanyWithAddress> {
    try {
      console.log('Saving company data')
      const createData = this.transformFormDataToDto(formData)
      if (creditApplicationId) {
        createData.creditApplicationId = creditApplicationId
      }
      console.log('Transformed data:', createData)

      // For now, we'll always use the complete endpoint
      // The backend should handle create vs update logic internally
      return await this.createCompanyComplete(createData)
    } catch (error) {
      console.error('Error saving company data:', error)
      throw error
    }
  }

  // Helper function to transform frontend form data to backend DTO
  transformFormDataToDto(formData: any): any {
    return {
      // Company information - only required fields
      companyName: formData.companyName,
      businessName:
        formData.socialReason || formData.businessName || formData.companyName, // Use socialReason from page form
      rfc: formData.rfc.toUpperCase(),
      email: formData.email,
      phoneNumber: formData.phone,
      website: formData.website || undefined,
      // Optional fields - will use database defaults if not provided
      // economicActivity and taxRegime will use their default values from the entity

      // Address information - mapping from page form fields
      street: formData.street || formData.address,
      exteriorNumber: formData.exteriorNumber || 'S/N',
      interiorNumber: formData.interiorNumber || undefined,
      betweenStreets: formData.betweenStreets || '',
      postalCode: formData.postalCode || formData.zipCode,
      neighborhood: formData.colony || formData.neighborhood || '',
      city: formData.city,
      municipality: formData.municipality || undefined,
      state: formData.state,
      stateAbbreviation: formData.stateAbbreviation || undefined,
      country: 'México',

      // Operative Address
      operativeSameAsFiscalAddress:
        formData.operativeSameAsFiscalAddress || false,
      operativeStreet: formData.operativeStreet || '',
      operativeExteriorNumber: formData.operativeExteriorNumber || 'S/N',
      operativeInteriorNumber: formData.operativeInteriorNumber || undefined,
      operativeBetweenStreets: formData.operativeBetweenStreets || '',
      operativePostalCode: formData.operativePostalCode || '',
      operativeColony: formData.operativeColony || '',
      operativeCity: formData.operativeCity || '',
      operativeMunicipality: formData.operativeMunicipality || '',
      operativeState: formData.operativeState || '',
      operativeStateAbbreviation: formData.operativeStateAbbreviation || '',
    }
  }
}

export default new CompanyService()
